import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { TableTopPanel } from 'components/TableTopPanel';
import { Toolbar } from 'components/Toolbar';
import { useAccess } from 'hooks/useAccess';
import { Button, cnMixSpace } from 'uikit';
import { destroyAgGridTableFilters } from 'utils';

import type { AgGridReact } from 'ag-grid-react';
import cn from 'classnames';
import { useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useCatalog } from '../CatalogList/hooks/useCatalog';
import { CatalogTablePage } from '../Common/CatalogTablePage';

import { CreateOrEditConstructionTypeModal } from './CreateOrEditConstructionTypeModal';
import { useCatalogConstructionTypesColumns } from './hooks/useCatalogConstructionTypesColumns';
import { useGetCatalogConstructionTypes } from './hooks/useGetCatalogConstructionTypes';
import type { TCatalogConstructionTypeRow } from './types';

import Styles from './catalogConstructionTypes.module.css';

export const CatalogConstructionTypes = () => {
    const [isCreating, setIsCreating] = useState(false);
    const [isCreateOrEditModalOpened, setIsCreateOrEditModalOpened] = useState(false);
    const [dataEditModal, setDataEditModal] = useState<TCatalogConstructionTypeRow | null>(null);
    const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});

    const { catalogs: catalogsRules } = useAccess();
    const { id } = useParams();
    const { catalogData } = useCatalog(id);

    const gridRef = useRef<AgGridReact<TCatalogConstructionTypeRow> | null>(null);
    const tableApi = useMemo(() => (gridRef && 'current' in gridRef ? gridRef.current : undefined), [gridRef]);

    const catalogName = catalogData?.data?.name || 'Справочник видов строительства';
    const catalogId = catalogData?.data?.id;
    const methodologies = catalogData?.data?.type?.methodologies || [];

    const refreshData = () => {
        if (gridRef.current?.api) {
            gridRef.current.api.refreshInfiniteCache();
        }
    };

    const handleCreateConstructionType = () => {
        setDataEditModal(null);
        setIsCreating(true);
        setIsCreateOrEditModalOpened(true);
    };

    const createOrEditModalHandler = (nextState: boolean) => {
        setIsCreateOrEditModalOpened(nextState);
        setIsCreating(false);
    };

    const onRemoveSuccess = () => {
        refreshData();
        destroyAgGridTableFilters({
            api: gridRef.current?.api,
            columns: ['name'],
        });
        setFilterValues({});
    };

    const { columns, isColumnVisible, tableSettingsConfirm, fullSetSettingsColumns } =
        useCatalogConstructionTypesColumns({
            createOrEditModalHandler,
            setDataEditModal,
            onRemoveSuccess,
            setFilterValues,
            catalogsRules,
            catalogUniqueId: id,
        });

    const { datasource, totalCount, fetchedCount } = useGetCatalogConstructionTypes({
        filterValues,
        id,
    });

    return (
        <CatalogTablePage catalogName={catalogName} methodology={methodologies} isError={false} isLoading={false}>
            <Toolbar>
                <div className={Styles.ContentToolbar}>
                    <Button
                        disabled={!catalogsRules.canCreate || !catalogId}
                        label="Создать вид строительства"
                        onClick={handleCreateConstructionType}
                        size="s"
                        view="secondary"
                    />
                </div>
            </Toolbar>
            <div
                className={cn(
                    Styles.table,
                    cnMixSpace({
                        mV: 'l',
                        mH: 'xl',
                    }),
                )}
            >
                <TableTopPanel
                    totalItems={totalCount}
                    fetchedItems={fetchedCount}
                    tableApi={tableApi}
                    optionalColumns={fullSetSettingsColumns}
                    isColumnVisible={isColumnVisible}
                    handleTableSettingsConfirm={tableSettingsConfirm}
                    showPinColumn
                />
                <AgGridTable
                    className={Styles.ConstructionTypesTable}
                    suppressCellFocus
                    rowModelType="infinite"
                    ref={gridRef}
                    columnDefs={columns}
                    maxConcurrentDatasourceRequests={1}
                    maxBlocksInCache={50}
                    cacheBlockSize={50}
                    datasource={datasource}
                    context={{
                        tableUniqueId: 'catalogConstructionTypes',
                    }}
                    headerHeight={40}
                />
            </div>
            {isCreateOrEditModalOpened && (
                <CreateOrEditConstructionTypeModal
                    isModalOpened={isCreateOrEditModalOpened}
                    closeHandler={createOrEditModalHandler}
                    isCreating={isCreating}
                    dataEditModal={dataEditModal}
                    idCatalog={catalogId}
                    refreshData={refreshData}
                    gridRefApi={gridRef.current?.api}
                    setFilterValues={setFilterValues}
                />
            )}
        </CatalogTablePage>
    );
};

CatalogConstructionTypes.displayName = 'CatalogConstructionTypes';
